import {
  getCore as get,
  postCore as post,
  putCore as put,
  delCore as del,
  fetchWithAuth,
} from "../utils/httpInterceptor";

const aiDevelopmentService = {
  projects: () => get("/api/ai-development/projects"),
  createProject: (body) => post("/api/ai-development/projects", body),
  updateProject: (id, body) => put(`/api/ai-development/projects/${id}`, body),
  cancelProject: (id, version) =>
    post(`/api/ai-development/projects/${id}/cancel`, { version }),
  stories: (status = "", page = 0, size = 25, projectId = "") =>
    get(
      `/api/ai-development/stories?status=${encodeURIComponent(status)}&page=${page}&size=${size}` +
        (projectId ? `&projectId=${encodeURIComponent(projectId)}` : ""),
    ),
  story: (id) => get(`/api/ai-development/stories/${id}`),
  createStory: (body) => post("/api/ai-development/stories", body),
  updateStory: (id, body) => put(`/api/ai-development/stories/${id}`, body),
  submit: (id) => post(`/api/ai-development/stories/${id}/submit`, {}),
  requestAnalysis: (id) =>
    post(`/api/ai-development/stories/${id}/analysis`, {}),
  plan: (id) => get(`/api/ai-development/stories/${id}/plan`),
  updateTask: (id, body) => put(`/api/ai-development/tasks/${id}`, body),
  taskComments: (id) => get(`/api/ai-development/tasks/${id}/comments`),
  addTaskComment: (id, bodyHtml) =>
    post(`/api/ai-development/tasks/${id}/comments`, { bodyHtml }),
  storyAttachments: (id) =>
    get(`/api/ai-development/stories/${id}/attachments`),
  taskAttachments: (id) => get(`/api/ai-development/tasks/${id}/attachments`),
  attachStoryImage: (id, file) => {
    const body = new FormData();
    body.append("file", file);
    return post(`/api/ai-development/stories/${id}/attachments`, body);
  },
  attachTaskImage: (id, file, commentId = "") => {
    const body = new FormData();
    body.append("file", file);
    const q = commentId ? `?commentId=${commentId}` : "";
    return post(`/api/ai-development/tasks/${id}/attachments${q}`, body);
  },
  attachmentBlob: async (id) => {
    const response = await fetchWithAuth(
      `/api/ai-development/attachments/${id}/content`,
      {
        method: "GET",
        baseUrl:
          process.env.REACT_APP_CORE_BACKEND_URL ||
          process.env.REACT_APP_LOAD_PAGE_URL,
      },
    );
    return response.blob();
  },
  findings: (id) => get(`/api/ai-development/stories/${id}/findings`),
  agentRuns: (id) => get(`/api/ai-development/stories/${id}/agent-runs`),
  events: (id, afterSequence = 0) =>
    get(
      `/api/ai-development/stories/${id}/events?afterSequence=${afterSequence}`,
    ),
  storyRelationships: (id) => get(`/api/ai-development/stories/${id}/relationships`),
  taskRelationships: (taskId) => get(`/api/ai-development/tasks/${taskId}/relationships`),
  createRelationship: (body) => post("/api/ai-development/relationships", body),
  deleteRelationship: (id, version) =>
    del(`/api/ai-development/relationships/${id}?version=${version}`),
  sprints: (projectId) =>
    get(`/api/ai-development/sprints?projectId=${encodeURIComponent(projectId)}`),
  createSprint: (body) => post("/api/ai-development/sprints", body),
  assignStoryToSprint: (sprintId, storyId) =>
    post(`/api/ai-development/sprints/${sprintId}/stories/${storyId}`, {}),
  removeStoryFromSprint: (storyId) =>
    del(`/api/ai-development/sprints/stories/${storyId}`),
  requestSprintPlanning: (sprintId) =>
    post(`/api/ai-development/sprints/${sprintId}/planning`, {}),
  decideSprintPlanning: (ceremonyId, body) =>
    post(`/api/ai-development/sprints/ceremonies/${ceremonyId}/decision`, body),
  startSprint: (sprintId, version) =>
    post(`/api/ai-development/sprints/${sprintId}/start?version=${version}`, {}),
  recordSprintCeremony: (sprintId, body) =>
    post(`/api/ai-development/sprints/${sprintId}/ceremonies`, body),
  sprintCeremonies: (sprintId) =>
    get(`/api/ai-development/sprints/${sprintId}/ceremonies`),
  completeSprint: (sprintId, version) =>
    post(`/api/ai-development/sprints/${sprintId}/complete?version=${version}`, {}),
  cancelSprint: (sprintId, body) =>
    post(`/api/ai-development/sprints/${sprintId}/cancel`, body),
  approvePlan: (id, comment = "") =>
    post(`/api/ai-development/stories/${id}/plan/approve`, { comment }),
  rejectPlan: (id, comment = "") =>
    post(`/api/ai-development/stories/${id}/plan/reject`, { comment }),
  resume: (id) => post(`/api/ai-development/stories/${id}/resume`, {}),
  toolingDiff: (id) => get(`/api/ai-development/stories/${id}/tooling/diff`),
  storyComments: (id) => get(`/api/ai-development/stories/${id}/comments`),
  addStoryComment: (id, bodyHtml) =>
    post(`/api/ai-development/stories/${id}/comments`, { bodyHtml }),
  taigaSyncConfig: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/taiga-sync`),
  updateTaigaSyncConfig: (projectId, body) =>
    put(`/api/ai-development/projects/${projectId}/taiga-sync`, body),
  triggerTaigaReconcile: (projectId) =>
    post(`/api/ai-development/projects/${projectId}/taiga-sync/reconcile`, {}),
  taigaProjectConflicts: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/taiga-sync/conflicts`),
  storyTaigaStatus: (id) => get(`/api/ai-development/stories/${id}/taiga-sync`),
  retryTaigaSync: (id) =>
    post(`/api/ai-development/stories/${id}/taiga-sync/retry`, {}),
  excludeFromTaigaSync: (id) =>
    post(`/api/ai-development/stories/${id}/taiga-sync/exclude`, {}),
  includeInTaigaSync: (id) =>
    post(`/api/ai-development/stories/${id}/taiga-sync/include`, {}),
  resolveTaigaConflict: (conflictId, body) =>
    post(`/api/ai-development/taiga-sync/conflicts/${conflictId}/resolve`, body),
  jiraScrumBoards: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/jira-sync/boards`),
  jiraSyncConfig: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/jira-sync`),
  updateJiraSyncConfig: (projectId, body) =>
    put(`/api/ai-development/projects/${projectId}/jira-sync`, body),
  triggerJiraReconcile: (projectId) =>
    post(`/api/ai-development/projects/${projectId}/jira-sync/reconcile`, {}),
  jiraProjectConflicts: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/jira-sync/conflicts`),
  storyJiraStatus: (id) => get(`/api/ai-development/stories/${id}/jira-sync`),
  retryJiraSync: (id) =>
    post(`/api/ai-development/stories/${id}/jira-sync/retry`, {}),
  excludeFromJiraSync: (id) =>
    post(`/api/ai-development/stories/${id}/jira-sync/exclude`, {}),
  includeInJiraSync: (id) =>
    post(`/api/ai-development/stories/${id}/jira-sync/include`, {}),
  resolveJiraConflict: (conflictId, body) =>
    post(`/api/ai-development/jira-sync/conflicts/${conflictId}/resolve`, body),
  azureDevOpsSyncConfig: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/azure-devops-sync`),
  updateAzureDevOpsSyncConfig: (projectId, body) =>
    put(`/api/ai-development/projects/${projectId}/azure-devops-sync`, body),
  triggerAzureDevOpsReconcile: (projectId) =>
    post(`/api/ai-development/projects/${projectId}/azure-devops-sync/reconcile`, {}),
  azureDevOpsProjectConflicts: (projectId) =>
    get(`/api/ai-development/projects/${projectId}/azure-devops-sync/conflicts`),
  storyAzureDevOpsStatus: (id) => get(`/api/ai-development/stories/${id}/azure-devops-sync`),
  retryAzureDevOpsSync: (id) =>
    post(`/api/ai-development/stories/${id}/azure-devops-sync/retry`, {}),
  excludeFromAzureDevOpsSync: (id) =>
    post(`/api/ai-development/stories/${id}/azure-devops-sync/exclude`, {}),
  includeInAzureDevOpsSync: (id) =>
    post(`/api/ai-development/stories/${id}/azure-devops-sync/include`, {}),
  resolveAzureDevOpsConflict: (conflictId, body) =>
    post(`/api/ai-development/azure-devops-sync/conflicts/${conflictId}/resolve`, body),
  executionProviders: () => get("/api/ai-development/execution-providers"),
  globalExecutionSettings: () =>
    get("/api/ai-development/global-execution-settings"),
  updateGlobalExecutionSettings: (body) =>
    put("/api/ai-development/global-execution-settings", body),
  requestProductOwnerReview: (storyId) =>
    post(`/api/ai-development/stories/${storyId}/product-owner-review`, {}),
  productOwnerReviews: (storyId) =>
    get(`/api/ai-development/stories/${storyId}/product-owner-reviews`),
  decideProductOwnerReview: (reviewId, body) =>
    post(`/api/ai-development/product-owner-reviews/${reviewId}/decision`, body),
  proposeBacklogOrdering: (projectId) =>
    post(`/api/ai-development/projects/${projectId}/backlog-ordering`, {}),
  enrichCeremonyWithAgent: (ceremonyId) =>
    post(`/api/ai-development/sprints/ceremonies/${ceremonyId}/agent-enrich`, {}),
  ceremonyAgentContent: (ceremonyId) =>
    get(`/api/ai-development/sprints/ceremonies/${ceremonyId}/agent-content`),
};

export default aiDevelopmentService;
